import React, { useCallback, useState } from 'react';
import { createColumnsForObjectData, SalvTable } from '@salvapp/ui-components';
import RateLimiterTableHeader, { ADD_NEW_GROUP } from './RateLimiterTableHeader';
import { InternalApiRateLimitEndpoint } from '../../api-internal';
import RateLimiterGroupEditModal, { RateLimiterGroupModalState } from './RateLimiterGroupEditModal';
import { deleteRateLimitBucket, updateDefaultRateLimit, updateRateLimitBucket } from './ratelimitActions';
import { RateLimitBucket } from './RateLimiterConfig';

interface Props {
  bucket: RateLimitBucket;
  allBuckets: RateLimitBucket[];
  moveEndPoints: (
    originalBucket: RateLimitBucket,
    newBucket: RateLimitBucket,
    paths: InternalApiRateLimitEndpoint[]
  ) => Promise<void>;
  refresh: () => void;
  isDefaultGroup: boolean;
}

interface ModalState {
  isModalOpen: boolean;
  state: RateLimiterGroupModalState;
}

const RateLimiterTable = ({ bucket, allBuckets, moveEndPoints, refresh, isDefaultGroup }: Props) => {
  const [selectedEndpoints, setSelectedEndpoints] = useState<InternalApiRateLimitEndpoint[]>([]);

  const [modalState, setModalState] = useState<ModalState>({
    isModalOpen: false,
    state: RateLimiterGroupModalState.CREATE_NEW_GROUP,
  });

  const onEdit = useCallback(() => {
    if (isDefaultGroup) {
      setModalState({ isModalOpen: true, state: RateLimiterGroupModalState.EDIT_DEFAULT_GROUP });
    } else {
      setModalState({ isModalOpen: true, state: RateLimiterGroupModalState.EDIT_GROUP });
    }
  }, [isDefaultGroup]);

  const setShow = useCallback((show: boolean) => {
    setModalState((prevState) => ({ ...prevState, isModalOpen: show }));
  }, []);

  const onGroupSelect = useCallback(
    async (groupId: string) => {
      if (groupId === ADD_NEW_GROUP) {
        setModalState({ isModalOpen: true, state: RateLimiterGroupModalState.CREATE_NEW_GROUP });
      } else {
        const newGroup = allBuckets.find((it) => it.id === groupId);
        if (newGroup) {
          await moveEndPoints(bucket, newGroup, selectedEndpoints);
          setSelectedEndpoints([]);
          refresh();
        }
      }
    },
    [moveEndPoints, bucket, selectedEndpoints]
  );

  const onFormSubmit = useCallback(
    async (bucketName: string, limitPerSecond: number) => {
      switch (modalState.state) {
        case RateLimiterGroupModalState.CREATE_NEW_GROUP: {
          const newBucket = {
            id: 'new-id',
            bucketName,
            limitPerSecond,
            endpoints: [],
          };
          await moveEndPoints(bucket, newBucket, selectedEndpoints);
          setSelectedEndpoints([]);
          setModalState({ isModalOpen: false, state: RateLimiterGroupModalState.CREATE_NEW_GROUP });
          break;
        }
        case RateLimiterGroupModalState.EDIT_GROUP: {
          await deleteRateLimitBucket(bucket.bucketName);
          await updateRateLimitBucket({ id: bucket.id, bucketName, limitPerSecond, endpoints: bucket.endpoints });
          setSelectedEndpoints([]);
          setModalState({ isModalOpen: false, state: RateLimiterGroupModalState.EDIT_GROUP });
          break;
        }
        case RateLimiterGroupModalState.EDIT_DEFAULT_GROUP: {
          await updateDefaultRateLimit(limitPerSecond);
          setSelectedEndpoints([]);
          setModalState({ isModalOpen: false, state: RateLimiterGroupModalState.EDIT_DEFAULT_GROUP });
          break;
        }
      }
      refresh();
    },
    [modalState.state, bucket, selectedEndpoints]
  );

  return (
    <>
      <SalvTable<InternalApiRateLimitEndpoint>
        className='rate-limiter-table'
        title={
          <RateLimiterTableHeader
            text={bucket.bucketName}
            limit={bucket.limitPerSecond === -1 ? 'Not set' : bucket.limitPerSecond.toString()}
            availableBuckets={allBuckets.filter((it) => it.id !== bucket.id)}
            showEditButton={selectedEndpoints.length === 0}
            onEdit={onEdit}
            onGroupSelect={onGroupSelect}
          />
        }
        columns={createColumnsForObjectData<InternalApiRateLimitEndpoint>([
          {
            accessorKey: 'endpointPath',
            header: 'Url',
          },
        ])}
        selectedRows={selectedEndpoints}
        setSelectedRows={(endPoints: InternalApiRateLimitEndpoint[]) => setSelectedEndpoints(endPoints)}
        data={bucket.endpoints}
      />
      {modalState.isModalOpen && (
        <RateLimiterGroupEditModal
          bucket={bucket}
          setShow={setShow}
          state={modalState.state}
          onSubmit={onFormSubmit}
          otherBucketNames={allBuckets.filter((it) => it.id !== bucket.id).map((it) => it.bucketName)}
        />
      )}
    </>
  );
};

export default RateLimiterTable;
